import { Images } from 'assets/images/images.index';
import axios from 'axios';
import { PopUpInformative } from 'components';
import { Button } from 'components/atoms/Button/Button';
import { Input } from 'components/atoms/Input/Input';
import { IOptionSelect, Select } from 'components/atoms/Select/Select';
import { HeaderWithSteps } from 'components/organisms/Header-with-steps/Header-with-steps';
import { PopupEmailSend } from 'components/templates/Popup-email-send/Popup-email-send';
import { PopupErrorRecoverPassword } from 'components/templates/Popup-error-recover-password/Popup-error-recover-password';
import { FirebaseError } from 'firebase/app';
import { useFormik } from 'formik';
import { TypeState } from 'models/app.interface';
import { IDetailStep } from 'models/info.steps.interface';
import { IAppReducer } from 'models/reducer.interface';
import { setCreateAccountInformation } from 'pages/Create-account/store/actions/create-account.types';
import { setUser } from 'pages/sing-in/store/actions/auth.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { handleRegisterWithCustomToken } from 'shared/services/apiSales';
import {
  DataStoreFlags,
  getDataStoreFlags,
} from 'shared/services/flags.service';
import { getPlanByCompanyId } from 'shared/services/plans';
import {
  impSales,
  ImpersonateCandidate,
  isImpersonateNeedsSelection,
} from 'shared/services/user';
import { setBranch } from 'store/actions/branch.types';
import { getAgentSourceFields } from 'utils/agent-source.utils';
import {
  IFormForgotPassword,
  IFormForgotPasswordErrors,
  SalesSearchBy,
} from './models/Sales.interface';
import './Sales.scss';

function classifySalesImpersonationError(error: unknown): TypeState {
  if (error instanceof FirebaseError) {
    return error.code === 'auth/user-not-found' ? 'successful' : 'error';
  }
  if (axios.isAxiosError(error)) {
    if (
      error.response?.status === 400 &&
      error.response.data?.error?.message?.toLowerCase() === 'user not found'
    ) {
      return 'info';
    }
    return 'error';
  }
  return 'error';
}

const SEARCH_BY_OPTIONS: IOptionSelect[] = [
  { value: 'email', description: 'Correo electrónico' },
  { value: 'phone', description: 'Teléfono' },
];

const PHONE_DIAL_OPTIONS: IOptionSelect[] = [
  { value: '+57', description: 'Colombia (+57)' },
  { value: '+1', description: 'Puerto Rico (+1)' },
  { value: '+506', description: 'Costa Rica (+506)' },
  { value: '+52', description: 'México (+52)' },
  { value: '+507', description: 'Panamá (+507)' },
];

export const Sales = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    authReducer: user,
    branchReducer: { branch },
  } = useSelector((state: IAppReducer) => state);

  const [popUp, setPopUp] = useState<{
    isVisible: boolean;
    type: TypeState;
  }>({ isVisible: false, type: 'successful' });
  const [flags, setFlags] = useState<DataStoreFlags | null>(null);
  const [impersonateCandidates, setImpersonateCandidates] = useState<
    ImpersonateCandidate[] | null
  >(null);
  const [selectingCandidateId, setSelectingCandidateId] = useState<
    number | null
  >(null);
  const isTester = user?.isTester ?? false;

  useEffect(() => {
    getDataStoreFlags().then(setFlags);
  }, []);

  const completeImpersonationAfterFetch = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (customRegister: any) => {
      const response = await getPlanByCompanyId(
        customRegister.company.id.toString()
      );
      const userWithToken = await handleRegisterWithCustomToken(
        customRegister.customToken
      );
      const tknFirebase = await userWithToken.getIdToken();

      dispatch(
        setUser({
          role: customRegister.role.name.toLocaleLowerCase(),
          countryAcronym: customRegister.country_id.acronym,
          userId: customRegister.id,
          uid: customRegister.auth_id,
          name: customRegister.name,
          lastname: customRegister.lastname,
          email: customRegister.email,
          accessToken: user.accessToken,
          userAuth: {
            phone: customRegister.phone,
            phone_country_code: customRegister.phone_country_code,
            country: {
              countryCode: customRegister.indicator,
            },
          },
          agentId: customRegister.agentId,
          companyId: customRegister.company.id,
          plansId: response.join(','),
          formToShow: customRegister.company?.formToShow || null,
          tknFirebase: tknFirebase,
          isTester: !!customRegister.is_tester,
          agentAuthData: {
            userId: user.userId,
            uid: user.uid,
            name: user.name,
            lastname: user.lastname,
            email: user.email,
            accessToken: user.accessToken,
            branch,
            userAuth: {
              phone: user.phone,
              phone_country_code: user.indicator,
            },
            plansId: user.plansId,
            companyId: user.companyId,
            role: user.role,
            countryAcronym: user.countryAcronym,
            tknFirebase: user.tknFirebase,
            isTester: user.isTester,
            ...getAgentSourceFields(user),
          },
        })
      );
      dispatch(setBranch(customRegister.country_id.acronym));
      navigate('/settings');
    },
    [branch, dispatch, navigate, user]
  );

  const formForgotPasswordInitialValues: IFormForgotPassword = {
    searchBy: 'email',
    email: '',
    phoneDialCode: '+1',
    phoneLocal: '',
  };
  const steps: IDetailStep[] = [
    {
      description: 'Cambio de cuenta ',
      state: 'active',
      link: '/forgot-password',
      permissionToNavigate: false,
    },
  ];
  const formForgotPasswordValidate = (values: IFormForgotPassword) => {
    const errors: IFormForgotPasswordErrors = {};
    if (values.searchBy === 'email') {
      if (!values.email) {
        errors.email = 'Este campo es requerido';
      } else if (
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
      ) {
        errors.email = 'Formato de correo electrónico incorrecto';
      }
    } else {
      const digits = values.phoneLocal.replace(/\D/g, '');
      if (!digits) {
        errors.phoneLocal = 'Este campo es requerido';
      } else if (digits.length < 7 || digits.length > 15) {
        errors.phoneLocal = 'Ingrese un número válido (7 a 15 dígitos)';
      }
    }
    return errors;
  };
  const formForgotPassword = useFormik({
    initialValues: formForgotPasswordInitialValues,
    validate: formForgotPasswordValidate,
    onSubmit: async (values) => {
      let failureType: TypeState | null = null;
      try {
        const { sourceApp, sourceOrigin } = getAgentSourceFields(user);
        const result = await impSales(
          values.searchBy === 'email'
            ? {
                email: values.email.trim().toLowerCase(),
                phone: null,
                phone_country_code: null,
                source: sourceApp,
                origin: sourceOrigin,
              }
            : {
                email: null,
                phone: values.phoneLocal.replace(/\D/g, ''),
                phone_country_code: values.phoneDialCode,
                source: sourceApp,
                origin: sourceOrigin,
              }
        );
        if (isImpersonateNeedsSelection(result)) {
          setImpersonateCandidates(result.candidates);
          return;
        }
        await completeImpersonationAfterFetch(result);
      } catch (error: unknown) {
        failureType = classifySalesImpersonationError(error);
      } finally {
        if (failureType !== null) {
          const nextType = failureType;
          setPopUp((prev) => ({
            ...prev,
            isVisible: true,
            type: nextType,
          }));
        }
      }
    },
  });

  const handleSelectImpersonateCandidate = useCallback(
    async (candidateId: number) => {
      setSelectingCandidateId(candidateId);
      let failureType: TypeState | null = null;
      try {
        const { sourceApp, sourceOrigin } = getAgentSourceFields(user);
        const result = await impSales({
          email: null,
          phone: null,
          phone_country_code: null,
          id: candidateId,
          source: sourceApp,
          origin: sourceOrigin,
        });
        if (isImpersonateNeedsSelection(result)) {
          setImpersonateCandidates(result.candidates);
          return;
        }
        setImpersonateCandidates(null);
        await completeImpersonationAfterFetch(result);
      } catch (error: unknown) {
        failureType = classifySalesImpersonationError(error);
      } finally {
        if (failureType !== null) {
          const nextType = failureType;
          setPopUp((prev) => ({
            ...prev,
            isVisible: true,
            type: nextType,
          }));
        }
        setSelectingCandidateId(null);
      }
    },
    [completeImpersonationAfterFetch]
  );

  const showInfoPopUp = () => {
    setPopUp({ ...popUp, isVisible: !popUp.isVisible });
  };

  const email = useMemo(
    () =>
      formForgotPassword.values.searchBy === 'email'
        ? formForgotPassword.values.email
        : '',
    [formForgotPassword.values.searchBy, formForgotPassword.values.email]
  );

  const handleSearchByChange = (value: string | number) => {
    formForgotPassword.setFieldValue('searchBy', value as SalesSearchBy);
    void formForgotPassword.validateForm();
  };

  const handlePhoneDialChange = (value: string | number) => {
    formForgotPassword.setFieldValue('phoneDialCode', String(value));
  };

  const handleClosePopUp = () => {
    setPopUp({ isVisible: false, type: 'error' });
  };

  const handleCreateUser = useCallback(() => {
    dispatch(setCreateAccountInformation({ email }));
    navigate('/sales/personal-information', {
      state: { optionBtn: '/settings' },
    });
  }, [dispatch, email, navigate]);

  const handleCreateSubscription = useCallback(() => {
    dispatch(setCreateAccountInformation({ email }));
    navigate('/sales/personal-information', {
      state: { optionBtn: '/settings/subscriptions/add-subscription' },
    });
  }, [dispatch, email, navigate]);

  const handleCreateInsurance = useCallback(() => {
    dispatch(setCreateAccountInformation({ email }));
    navigate('/sales/personal-information', {
      state: { optionBtn: '/settings/subscriptions/create-insurance' },
    });
  }, [dispatch, email, navigate]);

  return (
    <>
      <HeaderWithSteps
        notProgress={true}
        steps={steps}
        onClick={() => {
          navigate('/settings/profile');
        }}
      />
      <form
        className="forgor__pasword__container container__generic"
        onSubmit={formForgotPassword.handleSubmit}
        noValidate
      >
        <h6>
          Ingrese el correo electrónico o el teléfono asociado a la cuenta.
        </h6>
        <p className="forgor__pasword__message">...</p>
        <Select
          id="sales-search-by"
          name="searchBy"
          placeholder="Buscar por"
          value={formForgotPassword.values.searchBy}
          options={SEARCH_BY_OPTIONS}
          onClick={handleSearchByChange}
          onBlur={formForgotPassword.handleBlur}
          isTouched={formForgotPassword.touched.searchBy}
          isRequired={true}
        />
        {formForgotPassword.values.searchBy === 'email' ? (
          <Input
            label="Correo Electrónico"
            id="login-email"
            type="email"
            name="email"
            onChange={formForgotPassword.handleChange}
            onBlur={formForgotPassword.handleBlur}
            value={formForgotPassword.values.email}
            placeholder=""
            isTouched={formForgotPassword.touched.email}
            errorInput={formForgotPassword.errors.email}
            errorLabel={formForgotPassword.errors.email}
            isRequired={true}
            form={formForgotPassword}
          />
        ) : (
          <div className="sales__phone-row">
            <div className="sales__phone-dial">
              <Select
                id="sales-phone-dial"
                name="phoneDialCode"
                placeholder="Indicativo"
                value={formForgotPassword.values.phoneDialCode}
                options={PHONE_DIAL_OPTIONS}
                onClick={handlePhoneDialChange}
                onBlur={formForgotPassword.handleBlur}
                isTouched={formForgotPassword.touched.phoneDialCode}
                isRequired={true}
              />
            </div>
            <div className="sales__phone-local">
              <Input
                label="Número de teléfono"
                id="sales-phone-local"
                type="tel"
                name="phoneLocal"
                onChange={formForgotPassword.handleChange}
                onBlur={formForgotPassword.handleBlur}
                value={formForgotPassword.values.phoneLocal}
                placeholder=""
                isTouched={formForgotPassword.touched.phoneLocal}
                errorInput={formForgotPassword.errors.phoneLocal}
                errorLabel={formForgotPassword.errors.phoneLocal}
                isRequired={true}
                form={formForgotPassword}
              />
            </div>
          </div>
        )}
        <Button
          color="primary"
          size="medium"
          template="primary"
          typeStyle="rounded"
          className="forgor__pasword__button"
          type="submit"
          isDisabled={
            formForgotPassword.isSubmitting ||
            !formForgotPassword.isValid ||
            !formForgotPassword.dirty ||
            impersonateCandidates !== null
          }
          isLoading={formForgotPassword.isSubmitting}
        >
          Cambiar <img src={Images.email} alt="" />
        </Button>
      </form>
      {popUp.isVisible &&
        (popUp.type === 'successful' ? (
          <PopupEmailSend
            email={formForgotPassword.values.email}
            customSubtitle={
              formForgotPassword.values.searchBy === 'phone'
                ? `Revise el número utilizado </br> ${
                    formForgotPassword.values.phoneDialCode
                  } ****${formForgotPassword.values.phoneLocal
                    .replace(/\D/g, '')
                    .slice(-4)}`
                : undefined
            }
            closePopUp={showInfoPopUp}
          />
        ) : popUp.type === 'info' ? (
          <PopUpInformative
            title={'Usuario no existe'}
            type={'info'}
            text={'No te preocupes selecciona una de las siguientes opciones:'}
            labelButton="Crear usuario"
            labelButton2="Crear suscripción"
            {...(isTester && flags?.insurance
              ? {
                  labelButton3: 'Crear seguro',
                  onClickButton3: handleCreateInsurance,
                }
              : {})}
            closePopUp={handleClosePopUp}
            onClickButton={handleCreateUser}
            onClickButton2={handleCreateSubscription}
          ></PopUpInformative>
        ) : (
          <PopupErrorRecoverPassword
            email={
              formForgotPassword.values.searchBy === 'email'
                ? formForgotPassword.values.email
                : `${
                    formForgotPassword.values.phoneDialCode
                  }${formForgotPassword.values.phoneLocal.replace(/\D/g, '')}`
            }
            closePopUp={showInfoPopUp}
          />
        ))}
      {impersonateCandidates && (
        <div
          className="sales__candidates-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sales-candidates-title"
        >
          <div className="sales__candidates-panel">
            <h2
              id="sales-candidates-title"
              className="sales__candidates-title"
            >
              Varias cuentas coinciden
            </h2>
            <p className="sales__candidates-hint">
              Elija la cuenta correcta. Revise correo y teléfono antes de
              continuar.
            </p>
            <ul className="sales__candidates-list">
              {impersonateCandidates.map((c) => {
                const fullName = `${c.name} ${c.lastName ?? c.lastname ?? ''}`.trim();
                const phoneLine = `${c.phone_country_code} ${c.phone}`.trim();
                const busy = selectingCandidateId === c.id;
                const locked = selectingCandidateId !== null;
                return (
                  <li key={c.id} className="sales__candidates-item">
                    <button
                      type="button"
                      className={`sales__candidate-card${
                        busy ? ' sales__candidate-card--busy' : ''
                      }`}
                      disabled={locked}
                      aria-busy={busy}
                      onClick={() =>
                        void handleSelectImpersonateCandidate(c.id)
                      }
                    >
                      <div className="sales__candidate-card__main">
                        <span className="sales__candidate-card__name">
                          {fullName}
                        </span>
                        <span className="sales__candidate-card__row">
                          <span className="sales__candidate-card__label">
                            Correo
                          </span>
                          <span className="sales__candidate-card__value">
                            {c.email}
                          </span>
                        </span>
                        <span className="sales__candidate-card__row">
                          <span className="sales__candidate-card__label">
                            Teléfono
                          </span>
                          <span className="sales__candidate-card__value">
                            {phoneLine}
                          </span>
                        </span>
                      </div>
                      {busy ? (
                        <span
                          className="sales__candidate-card__spinner"
                          aria-label="Cargando"
                        />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className="sales__candidates-cancel"
              disabled={selectingCandidateId !== null}
              onClick={() => setImpersonateCandidates(null)}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </>
  );
};
